import type { ElementType, FC, ReactNode } from 'react'

import Link from 'next/link'

import { cn } from '@/lib/utils'

interface RollTextProps {
    label: string
    className?: string
}

/* The reference rolls nav labels: the resting copy lifts out of a clipped box
   while an identical copy rises into its place. Two stacked spans, one
   transform, no JS. */
export const RollText: FC<RollTextProps> = ({ label, className }): ReactNode => {
    return (
        <span
            className={cn(
                'relative block overflow-hidden py-[0.18em] -my-[0.18em]',
                className
            )}
        >
            <span className='block transition-transform duration-[650ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/roll:-translate-y-full'>
                {label}
            </span>

            {/* Anchored at the padding edge, not inset-0, so the incoming copy
                lands exactly where the outgoing one left rather than 0.18em
                high. */}
            <span
                aria-hidden='true'
                className='absolute left-0 top-[0.18em] block w-full translate-y-full transition-transform duration-[650ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/roll:translate-y-0'
            >
                {label}
            </span>
        </span>
    )
}

interface RollLinkProps {
    href: string
    label: string
    className?: string
    textClassName?: string
    count?: string
    as?: ElementType
    onClick?: () => void
}

export const RollLink: FC<RollLinkProps> = ({
    href,
    label,
    className,
    textClassName,
    count,
    onClick
}): ReactNode => {
    return (
        <Link
            href={href}
            onClick={onClick}
            data-cursor='link'
            className={cn('group/roll inline-flex items-start gap-1', className)}
        >
            <RollText label={label} className={textClassName} />

            {count
                ? (
                    <span className='t-mono mt-[0.15em] text-[0.62em] text-brand'>
                        {count}
                    </span>
                )
                : null}
        </Link>
    )
}
